// ---------------------------------------------------------------------------
// ListView — workspaces list/picker rendered inside the drawer.
// ---------------------------------------------------------------------------

import {
  Badge,
  Body1,
  Button,
  Card,
  CardHeader,
  Spinner,
  Text,
  Toolbar,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Add20Filled,
  Add20Regular,
  Settings20Filled,
  Settings20Regular,
} from '@fluentui/react-icons';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useWorkspaceDrawer } from '../../context/WorkspaceDrawerContext.js';
import { useWorkspaces } from '../../hooks/useWorkspaces/useWorkspaces.js';
import type { WorkspaceSummary } from '../../types/workspace.js';

import { useStyles } from './WorkspaceDrawer.style.js';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const AddIcon = bundleIcon(Add20Filled, Add20Regular);
const SettingsIcon = bundleIcon(Settings20Filled, Settings20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

const EDIT_TOOLTIP_LABEL = 'Edit';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function ListView() {
  const styles = useStyles();
  const { workspaces, isLoading, error } = useWorkspaces();
  const { activeName, setActiveName } = useWorkspaceContext();
  const { open, close } = useWorkspaceDrawer();

  const handleSelect = (w: WorkspaceSummary) => {
    setActiveName(w.name);
    close();
  };

  return (
    <Card appearance="subtle" className={styles.body}>
      <Toolbar className={styles.listActions}>
        <Button
          appearance="primary"
          icon={<AddIcon />}
          onClick={() => open({ kind: 'create' })}
        >
          Create workspace
        </Button>
      </Toolbar>

      {isLoading && <Spinner label="Loading workspaces…" />}

      {!isLoading && error !== null && (
        <Card className={mergeClasses(styles.emptyState, styles.errorState)}>
          <Body1>Failed to load workspaces: {error}</Body1>
        </Card>
      )}

      {!isLoading && error === null && workspaces.length === 0 && (
        <Card className={styles.emptyState}>
          <Body1>You don&apos;t have any workspaces yet.</Body1>
          <Button
            appearance="primary"
            icon={<AddIcon />}
            onClick={() => open({ kind: 'create' })}
          >
            Create your first workspace
          </Button>
        </Card>
      )}

      {!isLoading && workspaces.length > 0 && (
        <Card appearance="subtle" className={styles.list}>
          {workspaces.map((w) => {
            const isActive = w.name === activeName;
            const lastModifiedDate = w.systemProperties?.lastModifiedDate;
            return (
              <Card
                key={w.name}
                className={mergeClasses(
                  styles.card,
                  isActive && styles.cardActive,
                )}
                role="button"
                tabIndex={0}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => handleSelect(w)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelect(w);
                  }
                }}
              >
                <CardHeader
                  className={styles.cardHeader}
                  header={
                    <Text className={styles.cardName}>
                      {w.displayName ?? w.name}
                    </Text>
                  }
                  description={
                    <Text className={styles.cardMeta}>
                      {w.name}
                      {lastModifiedDate && (
                        <> · last modified {formatDate(lastModifiedDate)}</>
                      )}
                    </Text>
                  }
                  action={
                    <Toolbar className={styles.cardRight}>
                      <Badge appearance="outline">
                        {w.accessControl == null ? 'Public' : 'Private'}
                      </Badge>
                      <Tooltip
                        content={EDIT_TOOLTIP_LABEL}
                        relationship="label"
                      >
                        <Button
                          appearance="subtle"
                          icon={<SettingsIcon />}
                          aria-label={`Settings for ${w.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            open({ kind: 'settings', name: w.name });
                          }}
                        />
                      </Tooltip>
                    </Toolbar>
                  }
                />
                {w.description && (
                  <Text className={styles.cardDescription}>
                    {w.description}
                  </Text>
                )}
              </Card>
            );
          })}
        </Card>
      )}
    </Card>
  );
}
